import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Button } from "@mui/material";
import { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import LandingScreenHeader from "../../components/Comman/LandingScreenHeader";
import tableStyles from "../../Styles/csvImportingValidation";
import {
    checkboxColor,
    lightPinkColor,
    offWhiteColor,
    white,
} from "../../config/ColorObj";
import {
    importOutletEnquiryMsg,
    noDataFoundMsg,
} from "../../config/messageconstant";

// Columns shown ONLY when isAdvanceEnquiryFlag === 1
const STANDARD_COLUMNS = [
    { key: "country", label: "Country", minWidth: 80, align: "center" },
    { key: "region", label: "Region", minWidth: 80, align: "left" },
    { key: "state", label: "State", minWidth: 80, align: "left" },
    { key: "zone", label: "Zone", minWidth: 80, align: "left" },
    { key: "district", label: "District", minWidth: 80, align: "left" },
    { key: "taluka", label: "Taluka", minWidth: 80, align: "left" },
    { key: "area", label: "Area", minWidth: 80, align: "left" },
    { key: "beat", label: "Beat", minWidth: 80, align: "left" },
    { key: "town", label: "Town", minWidth: 80, align: "left" },
    { key: "channel", label: "Channel", minWidth: 80, align: "left" },
    { key: "subChannel", label: "Sub Channel", minWidth: 120, align: "left" },
    { key: "outletType", label: "Outlet Type", minWidth: 120, align: "left" },
    { key: "distributorFirm", label: "Distributor Firm", minWidth: 140, align: "left" },
    { key: "distributorOwner", label: "Distributor Owner", minWidth: 150, align: "left" },
    { key: "firmName", label: "Firm Name", minWidth: 120, align: "left" },
    { key: "ownerName", label: "Owner Name", minWidth: 120, align: "left" },
    { key: "mobileNo", label: "Mobile No.", minWidth: 120, align: "center" },
    { key: "enquiryPlatform", label: "Enquiry Raised Platform", minWidth: 200, align: "left" },
    { key: "allocatedEmpCode", label: "Allocated Person Emp Code", minWidth: 240, align: "center" },
    { key: "potentialRate", label: "Potential Rate", minWidth: 130, align: "left" },
    { key: "errorMessage", label: "Error Message", minWidth: 130, align: "left" },
];

// Columns shown ONLY when isAdvanceEnquiryFlag === 1
const ADVANCE_COLUMNS = [
    { key: "enquiryDate", label: "Enquiry Date", minWidth: 130, align: "left" },
    { key: "firmName", label: "Firm Name", minWidth: 120, align: "left" },
    { key: "typeOfBusiness", label: "Type of Business", minWidth: 150, align: "left" },
    { key: "nameOfContactPerson", label: "Name of Contact Person", minWidth: 200, align: "left" },
    { key: "contactNo", label: "Contact No.", minWidth: 120, align: "center" },
    { key: "areaName", label: "Area", minWidth: 130, align: "left" },
    { key: "city", label: "City", minWidth: 130, align: "left" },
    { key: "stateName", label: "State", minWidth: 130, align: "left" },
    { key: "requirementType", label: "What are you looking for?", minWidth: 230, align: "left" },
    { key: "productsLookingFor", label: "Products Looking for", minWidth: 200, align: "left" },
    { key: "allocatedPersonRemark", label: "Remark if any", minWidth: 150, align: "left" },
    { key: "allocatedEmpCode", label: "Initial Enquiry Assigned to Emp Code", minWidth: 360, align: "center" },
    { key: "salesTeamContact", label: "Sales Person Contact No", minWidth: 200, align: "center" },
    { key: "finalOutcome", label: "Final Outcome", minWidth: 140, align: "left" },
    { key: "actionTaken", label: "Action Taken", minWidth: 140, align: "left" },
    { key: "errorMessage", label: "Error Message", minWidth: 250, align: "left" },
];

// Maps column key → row field accessor
const COLUMN_DATA_MAP = {
    country: (row) => row?.countryName,
    region: (row) => row?.regionName,
    state: (row) => row?.stateName,
    zone: (row) => row?.zoneName,
    district: (row) => row?.districtName,
    taluka: (row) => row?.talukaName,
    area: (row) => row?.areaName,
    beat: (row) => row?.beatName,
    town: (row) => row?.townName,
    channel: (row) => row?.channelName,
    subChannel: (row) => row?.subChannelName,
    outletType: (row) => row?.outletTypeName,
    distributorFirm: (row) => row?.distributorFirmName,
    distributorOwner: (row) => row?.distributorOwnerName,
    firmName: (row) => row?.firmName,
    ownerName: (row) => row?.ownerName,
    mobileNo: (row) => row?.mobileNo,
    enquiryPlatform: (row) => row?.enquiryRaisedPlatform,
    allocatedEmpCode: (row) => row?.allocatedPersonEmployeeCode,
    potentialRate: (row) => row?.potentialRateValue,
    errorMessage: (row, message) => (row.flag == 1 ? message : null),
    // Advance-only
    enquiryDate: (row) => row?.enquiryDate,
    typeOfBusiness: (row) => row?.typeOfBusiness,
    nameOfContactPerson: (row) => row?.nameOfContactPerson,
    contactNo: (row) => row?.contactNo,
    areaName: (row) => row?.areaName,
    city: (row) => row?.city,
    stateName: (row) => row?.stateName,
    requirementType: (row) => row?.requirementType,
    productsLookingFor: (row) => row?.productsLookingFor,
    allocatedPersonRemark: (row) => row?.allocatedPersonRemark,
    salesTeamContact: (row) => row?.salesTeamContact,
    forwardedToEmpCode: (row) => row?.forwardedToEmpCode,
    finalOutcome: (row) => row?.finalOutcome,
    actionTaken: (row) => row?.actionTaken,
};

const OutletEnquiryTable = ({
    onClickOfExport,
    onClickOfImport,
    searchValue,
    onSearchBox,
    getFilteredTableData,
    validatedProductList,
}) => {
    const isAdvanceEnquiryFlag = useMemo(
        () => Number(localStorage.getItem("isShowAdvanceEnquiryMaster") ?? 0),
        []
    );

    // Swap entire column set based on the flag
    const visibleColumns = useMemo(
        () => isAdvanceEnquiryFlag === 1 ? ADVANCE_COLUMNS : STANDARD_COLUMNS,
        [isAdvanceEnquiryFlag]
    );

    const filteredData = useMemo(
        () => getFilteredTableData(validatedProductList),
        [getFilteredTableData, validatedProductList, searchValue]
    );

    const handleMouseEnter = useCallback((e) => {
        e.currentTarget.style.backgroundColor =
            tableStyles.dataRowHover.backgroundColor;
    }, []);

    const handleMouseLeave = useCallback((e, row, index) => {
        e.currentTarget.style.backgroundColor =
            row.flag == 1
                ? lightPinkColor
                : index % 2 === 0
                    ? tableStyles.alternateRow.backgroundColor
                    : tableStyles.dataRow.backgroundColor;
    }, []);

    return (
        <>
            {/* ── Header row ── */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <LandingScreenHeader
                    screenTitle={importOutletEnquiryMsg}
                    showSaveBtnMain={false}
                    showAddButton={false}
                    showBackButton={false}
                />

                <div style={{ display: "flex", gap: 15 }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        startIcon={<CloudDownloadIcon />}
                        sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            transition: "all 0.2s",
                            minWidth: "120px",
                            height: "35px",
                            "&:hover": {
                                backgroundColor: offWhiteColor,
                                borderColor: checkboxColor,
                                color: checkboxColor,
                                transform: "translateY(-2px)",
                            },
                        }}
                        onClick={onClickOfExport}
                    >
                        Export
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        startIcon={<CloudUploadIcon sx={{ color: white }} />}
                        sx={{
                            color: white,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            transition: "all 0.2s",
                            minWidth: "150px",
                            height: "35px",
                            "&:hover": {
                                backgroundColor: checkboxColor,
                                transform: "translateY(-2px)",
                            },
                        }}
                        onClick={onClickOfImport}
                    >
                        Import
                    </Button>
                </div>
            </div>
            <br />
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "5px",
                }}
            >
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={onSearchBox}
                    style={{
                        padding: "6px 10px",
                        fontSize: "14px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        width: "230px",
                    }}
                />
            </div>

            {/* ── Table ── */}
            <div style={tableStyles.tableWrapper}>
                <table style={tableStyles.table}>
                    <thead style={tableStyles.stickyHeader}>
                        <tr style={tableStyles.headerRow}>
                            <th style={{ ...tableStyles.headerCell, minWidth: "60px" }}>
                                Sr.No
                            </th>
                            {visibleColumns.map((col) => (
                                <th
                                    key={col.key}
                                    style={{
                                        ...tableStyles.headerCell,
                                        minWidth: `${col.minWidth}px`,
                                        textAlign: col.align,
                                    }}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {validatedProductList.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={visibleColumns.length + 1}
                                    style={tableStyles.noData}
                                >
                                    {noDataFoundMsg}
                                </td>
                            </tr>
                        ) : (
                            filteredData.map((row, index) => {
                                const message = row.errorMsgs ? row.errorMsgs.join(", ") : "";
                                const isError = row.flag == 1;

                                return (
                                    <tr
                                        key={index + 1}
                                        style={{
                                            ...tableStyles.dataRow,
                                            ...(index % 2 === 0 ? tableStyles.alternateRow : {}),
                                            ...(isError && { backgroundColor: lightPinkColor }),
                                        }}
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={(e) => handleMouseLeave(e, row, index)}
                                    >
                                        <td
                                            style={{
                                                ...tableStyles.dataCell,
                                                minWidth: "60px",
                                                textAlign: "center",
                                            }}
                                        >
                                            {index + 1}
                                        </td>

                                        {visibleColumns.map((col) => {
                                            const getValue = COLUMN_DATA_MAP[col.key];
                                            const value = getValue(row, message);
                                            return (
                                                <td
                                                    key={col.key}
                                                    style={{
                                                        ...tableStyles.dataCell,
                                                        minWidth: `${col.minWidth}px`,
                                                        textAlign: col.align,
                                                    }}
                                                >
                                                    {value || "-"}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const mapStateToProps = () => ({});
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(OutletEnquiryTable);